/**
 * media-gallery-loop.js (v3)
 *
 * 商品ページのメディアギャラリーで、端からさらに送ろうとしたときに反対の端へループする。
 *   - 矢印クリック  : 最後で「次へ」→ 最初へ / 最初で「前へ」→ 最後へ
 *   - スワイプ(SP)  : 最後の画像でさらに左スワイプ → 最初へ / 最初で右スワイプ → 最後へ
 *
 * 設計方針:
 *   - global.js / product-media-gallery.liquid は編集しない
 *   - スライドの複製はしない（id・data-media-id の重複で media-gallery.js が壊れるため）
 *   - Dawn は constructor 内で bind したリスナーを登録するので prototype は書き換えず、
 *     インスタンス単位 + キャプチャフェーズで介入する
 *
 * 読み込み: sections/main-product.liquid
 *   <script src="{{ 'media-gallery-loop.js' | asset_url }}" defer="defer"></script>
 */
(() => {
  'use strict';

  // ---- 設定 ---------------------------------------------------------------

  // ループを有効にする範囲
  const SCOPE_SELECTOR = 'media-gallery';

  // 端でループ扱いにするスワイプ距離(px)。小さすぎると誤爆する
  const SWIPE_THRESHOLD = 50;

  // 端の判定許容誤差(px)。慣性スクロールの誤差を吸収する
  const EDGE_TOLERANCE = 2;

  // 'auto'   = 瞬間移動（画像枚数が多い場合はこちら推奨）
  // 'smooth' = 全画像をスクロールして戻る
  const JUMP_BEHAVIOR = 'auto';

  const DEBUG = true;

  // ------------------------------------------------------------------------

  const log = (...args) => {
    if (DEBUG) console.log('[media-gallery-loop]', ...args);
  };

  const initialized = new WeakSet();

  const getMaxScroll = (track) => track.scrollWidth - track.clientWidth;

  function jump(track, to) {
    const max = getMaxScroll(track);
    track.scrollTo({ left: to === 'first' ? 0 : max, behavior: JUMP_BEHAVIOR });
    log('loop →', to);
  }

  /** 矢印ボタンでのループ */
  function setupButtonLoop(comp, track) {
    const prevButton = comp.querySelector('button[name="previous"]');
    const nextButton = comp.querySelector('button[name="next"]');
    if (!nextButton) return;

    // Dawn の update() は enableSliderLooping が true なら
    // 端で disabled を付ける処理の手前で return する
    comp.enableSliderLooping = true;
    if (prevButton) prevButton.removeAttribute('disabled');
    nextButton.removeAttribute('disabled');

    // Dawn のリスナーはボタン自身に登録済みなので、
    // 祖先のキャプチャフェーズで先に横取りする
    comp.addEventListener(
      'click',
      (event) => {
        const button =
          event.target.closest && event.target.closest('button[name="previous"], button[name="next"]');
        if (!button || !comp.contains(button)) return;

        const max = getMaxScroll(track);
        if (max <= 0) return;

        const isNext = button.name === 'next';
        if (isNext && track.scrollLeft >= max - EDGE_TOLERANCE) {
          event.preventDefault();
          event.stopPropagation();
          jump(track, 'first');
        } else if (!isNext && track.scrollLeft <= EDGE_TOLERANCE) {
          event.preventDefault();
          event.stopPropagation();
          jump(track, 'last');
        }
      },
      true
    );
  }

  /** スワイプでのループ */
  function setupSwipeLoop(track) {
    let startX = null;
    let startY = null;
    let startedAtEnd = false;
    let startedAtStart = false;
    let pending = null; // 'first' | 'last' | null

    const reset = () => {
      startX = null;
      startY = null;
      pending = null;
    };

    track.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) {
          reset();
          return;
        }
        const max = getMaxScroll(track);
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        pending = null;

        // 「指を置いた時点ですでに端にいたか」で判定する。
        // これを見ないと、途中から端まで一気にスワイプしたときに
        // 最後の画像を飛ばしてループしてしまう
        startedAtEnd = max > 0 && track.scrollLeft >= max - EDGE_TOLERANCE;
        startedAtStart = max > 0 && track.scrollLeft <= EDGE_TOLERANCE;
      },
      { passive: true }
    );

    track.addEventListener(
      'touchmove',
      (event) => {
        if (startX === null || event.touches.length !== 1) return;

        const dx = event.touches[0].clientX - startX;
        const dy = event.touches[0].clientY - startY;

        // 縦方向の動きが大きい＝ページスクロールなので無視
        if (Math.abs(dy) > Math.abs(dx)) {
          pending = null;
          return;
        }

        if (startedAtEnd && dx < -SWIPE_THRESHOLD) {
          pending = 'first';
        } else if (startedAtStart && dx > SWIPE_THRESHOLD) {
          pending = 'last';
        } else {
          pending = null;
        }
      },
      { passive: true }
    );

    // 指を離してから動かす。移動中に scrollTo すると iOS で慣性と競合する
    track.addEventListener(
      'touchend',
      () => {
        if (pending) jump(track, pending);
        reset();
      },
      { passive: true }
    );

    track.addEventListener('touchcancel', reset, { passive: true });
  }

  function setup(comp) {
    if (initialized.has(comp)) return;

    const track = comp.querySelector('[id^="Slider-"]');
    if (!track) {
      log('skip (no track):', comp.id);
      return;
    }

    initialized.add(comp);
    setupButtonLoop(comp, track);
    setupSwipeLoop(track);
    log('initialized:', comp.id || comp.className);
  }

  function scan(root) {
    (root || document).querySelectorAll(`${SCOPE_SELECTOR} slider-component`).forEach(setup);
  }

  function start() {
    scan();

    // バリアント切替やセクション再描画で作り直された要素にも対応
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('slider-component') && node.closest(SCOPE_SELECTOR)) setup(node);
          if (node.querySelectorAll) scan(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.__mediaGalleryLoop = { scan, setup, SCOPE_SELECTOR };
})();